import { FinancialAccountSubtype } from '../../__generated__/globalTypes';

import {
  isCreditCardAccount,
  isDepositoryAccount,
  isInvestmentAccount,
  type IAccountTypeCheck,
  type ISupportedFinancialAccountOptions,
} from './accounts';

import type { FinancialAccount } from '../../hooks/queries/useFinancialAccounts';

/** Display order of the account sections. */
export const ACCOUNT_SECTIONS = ['checking', 'savings', 'creditCards', 'investments'] as const;

/** A group the app files accounts under; several subtypes can share one. */
export type AccountSection = (typeof ACCOUNT_SECTIONS)[number];

export type SectionTotalTone = 'success' | 'error';

interface ISectionPresentation {
  displayName: string;
  totalTone: SectionTotalTone;
}

const SECTION_PRESENTATION: Record<AccountSection, ISectionPresentation> = {
  checking: { displayName: 'Checking', totalTone: 'success' },
  savings: { displayName: 'Savings', totalTone: 'success' },
  creditCards: { displayName: 'Credit cards', totalTone: 'error' },
  investments: { displayName: 'Investments', totalTone: 'success' },
};

const DEPOSITORY_SECTIONS: Partial<Record<FinancialAccountSubtype, AccountSection>> = {
  [FinancialAccountSubtype.Checking]: 'checking',
  [FinancialAccountSubtype.Savings]: 'savings',
};

/** The section an account belongs to, or `null` for an account the app does not display. */
export function sectionOf(account: IAccountTypeCheck): AccountSection | null {
  if (isDepositoryAccount(account)) {
    return account.subtype ? (DEPOSITORY_SECTIONS[account.subtype] ?? null) : null;
  }
  if (isCreditCardAccount(account)) {
    return 'creditCards';
  }
  if (isInvestmentAccount(account)) {
    return 'investments';
  }
  return null;
}

export function sectionDisplayName(section: AccountSection): string {
  return SECTION_PRESENTATION[section].displayName;
}

/** Tone of a section's total: money owed reads as `error`, money held as `success`. */
export function sectionTotalTone(section: AccountSection): SectionTotalTone {
  return SECTION_PRESENTATION[section].totalTone;
}

/**
 * Files accounts under their sections, each sorted by bank name then account
 * name. Every section key is present; accounts with no section — and
 * investment accounts when `includeInvestments` is off — are left out.
 */
export function groupAccountsBySection(
  financialAccounts: FinancialAccount[],
  { includeInvestments }: ISupportedFinancialAccountOptions
): Record<AccountSection, FinancialAccount[]> {
  const grouped: Record<AccountSection, FinancialAccount[]> = {
    checking: [],
    savings: [],
    creditCards: [],
    investments: [],
  };

  for (const account of financialAccounts) {
    const section = sectionOf(account);
    if (section === null || (section === 'investments' && !includeInvestments)) {
      continue;
    }
    grouped[section].push(account);
  }

  for (const section of ACCOUNT_SECTIONS) {
    grouped[section].sort((a, b) => a.bank.name.localeCompare(b.bank.name) || a.name.localeCompare(b.name));
  }

  return grouped;
}
